import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Clock,
    Maximize,
    Minimize,
    Wifi,
    LogOut,
    User,
    Shield,
    RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import tenantService from '../../../services/tenantService';

/**
 * SuperAdminHeader Component
 * 
 * Header for Super Admin pages matching the shared Header component styling.
 */
export default function SuperAdminHeader() {
    const navigate = useNavigate();
    const [currentTime, setCurrentTime] = useState('');
    const [isMaximized, setIsMaximized] = useState(false);
    const [online, setOnline] = useState(navigator.onLine);
    const [showLogoutMenu, setShowLogoutMenu] = useState(false);
    const [reloadingDataSources, setReloadingDataSources] = useState(false);

    const username = localStorage.getItem('username') || 'Super Admin';

    // Live clock
    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            setCurrentTime(
                now.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                })
            );
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Sync fullscreen state
    useEffect(() => {
        const handleChange = () => setIsMaximized(Boolean(document.fullscreenElement));
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    // Online/offline state
    useEffect(() => {
        const goOnline = () => setOnline(true);
        const goOffline = () => setOnline(false);
        window.addEventListener('online', goOnline);
        window.addEventListener('offline', goOffline);
        return () => {
            window.removeEventListener('online', goOnline);
            window.removeEventListener('offline', goOffline);
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showLogoutMenu && !e.target.closest('.user-menu')) {
                setShowLogoutMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showLogoutMenu]);

    // Fullscreen toggle
    const toggleMaximize = useCallback(async () => {
        try {
            if (!document.fullscreenElement) {
                await document.documentElement.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch (e) {
            console.warn('Fullscreen toggle failed', e);
        }
    }, []);

    // Handle logout
    const handleLogout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('userType');
        toast.success('Logged out successfully');
        navigate('/platform/login');
        setShowLogoutMenu(false);
    }, [navigate]);

    // Reload data sources
    const handleReloadDataSources = async () => {
        setReloadingDataSources(true);
        try {
            await tenantService.superAdminReloadDataSources();
            toast.success("Datasources reloaded successfully");
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "Failed to reload datasources");
        } finally {
            setReloadingDataSources(false);
        }
    };

    return (
        <header className="bg-gray-900 shadow-xl">
            <div className="px-6 py-3 flex justify-between items-center">
                {/* Left Side */}
                <div className="flex items-center gap-4">
                    {/* Logo */}
                    <button
                        onClick={() => navigate('/platform/organizations')}
                        className="bg-white/10 p-2 rounded-lg shadow-xl flex items-center justify-center hover:bg-white/20 transition-colors"
                        title="Platform Admin"
                    >
                        <img
                            src="/loombiz-logo.png"
                            alt="Loombiz"
                            className="h-10 w-auto object-contain"
                        />
                    </button>

                    {/* Title */}
                    <div className="flex-1 min-w-0">
                        <h1
                            className="text-2xl font-black text-[#f08804] tracking-tighter leading-tight truncate"
                            style={{ letterSpacing: '0.05em' }}
                        >
                            Platform Console
                        </h1>
                        <div className="flex flex-wrap gap-2 mt-1">
                            <span className="bg-[#232F3E] text-white px-3 py-0.5 text-xs font-black rounded shadow-sm">
                                Super Admin
                            </span>
                            <span className="bg-gray-700 text-gray-300 px-3 py-0.5 text-xs font-bold rounded shadow-sm">
                                Master Database
                            </span>
                        </div>
                    </div>
                </div>

                {/* Right Side */}
                <div className="flex items-center gap-4">
                    {/* Reload Data Sources */}
                    <button
                        onClick={handleReloadDataSources}
                        disabled={reloadingDataSources}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50"
                        title="Reload Datasources"
                    >
                        <RefreshCw
                            size={16}
                            className={reloadingDataSources ? 'animate-spin' : ''}
                        />
                        <span className="hidden sm:inline">Reload Sources</span>
                    </button>

                    {/* Online indicator */}
                    <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 ${online ? 'bg-green-600/10 text-green-300' : 'bg-rose-600/10 text-rose-300'
                            }`}
                        title={online ? 'Online' : 'Offline'}
                    >
                        <Wifi size={16} />
                        <span className="text-xs font-medium">{online ? 'Online' : 'Offline'}</span>
                    </div>

                    {/* Clock */}
                    <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10">
                        <div className="flex items-center gap-2">
                            <Clock size={16} className="text-slate-300" />
                            <span className="font-bold text-white font-mono text-sm">{currentTime}</span>
                        </div>
                    </div>

                    {/* Fullscreen toggle */}
                    <button
                        onClick={toggleMaximize}
                        className={`p-2 rounded-lg transition shadow-md ${isMaximized
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-slate-700 hover:bg-slate-600 text-white'
                            }`}
                        title={isMaximized ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isMaximized ? <Minimize size={18} /> : <Maximize size={18} />}
                    </button>



                    {/* User Menu with Logout */}
                    <div className="relative user-menu">
                        <button
                            onClick={() => setShowLogoutMenu(!showLogoutMenu)}
                            className="bg-[#232F3E] hover:bg-[#37475A] p-2 rounded-lg transition shadow-md text-white flex items-center gap-2"
                            title={`Logged in as ${username}`}
                        >
                            <User size={18} />
                            <span className="text-sm font-medium hidden sm:inline">
                                {username}
                            </span>
                        </button>

                        {/* Dropdown Menu */}
                        {showLogoutMenu && (
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden z-50 animate-fadeIn">
                                {/* User Info */}
                                <div className="px-4 py-3 bg-gradient-to-r from-[#FFF9EE] to-purple-50 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-[#232F3E] rounded-full flex items-center justify-center">
                                            <Shield size={20} className="text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-gray-900 truncate">
                                                {username}
                                            </p>
                                            <p className="text-xs text-gray-600">
                                                Super Administrator
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Menu Items */}
                                <div className="py-1">
                                    {/* Logout */}
                                    <button
                                        onClick={handleLogout}
                                        className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-red-600 border-t border-gray-100"
                                    >
                                        <LogOut size={18} />
                                        <span className="text-sm font-semibold">Logout</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* CSS animation for dropdown */}
            <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
        </header>
    );
}
